import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatabaseOutlined } from '@ant-design/icons';
import { getEngineDisplay } from '../../../constants/databaseEngine';
import { CardActionMenu } from '../../../components/shared';

interface DatabaseCardProps {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  engine: string;
  version: string;
  instanceType: string;
  region: string;
  createdAt: string;
  onRefresh?: () => void;
}

const DatabaseCard: React.FC<DatabaseCardProps> = ({
  id,
  name,
  status,
  engine,
  version,
  instanceType,
  region,
  createdAt,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const { image } = getEngineDisplay(engine);
  const useImage = image && !imageError;

  const statusConfig = {
    running: { color: 'bg-green-500', text: 'Running', textColor: 'text-green-700' },
    stopped: { color: 'bg-gray-500', text: 'Stopped', textColor: 'text-gray-700' },
    deploying: { color: 'bg-blue-500', text: 'Deploying', textColor: 'text-blue-700' },
    error: { color: 'bg-red-500', text: 'Error', textColor: 'text-red-700' },
  };

  const currentStatus = statusConfig[status];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/databases/${id}`)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/databases/${id}`)}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-all hover:border-gray-300 dark:hover:border-gray-600 group cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${
              useImage ? 'bg-transparent' : 'bg-gradient-to-br from-purple-500 to-pink-600 text-white'
            }`}
          >
            {useImage ? (
              <img
                src={image}
                alt={engine}
                className="w-8 h-8 object-contain"
                onError={() => setImageError(true)}
              />
            ) : (
              <DatabaseOutlined className="text-lg" />
            )}
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
              {name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{engine} {version}</p>
          </div>
        </div>

        <CardActionMenu
          serviceId={id}
          status={status}
          detailRoute={`/databases/${id}`}
          serviceName={name}
          onRefresh={onRefresh}
        />
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Instance Type</span>
          <span className="text-gray-900 dark:text-white font-medium">{instanceType}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Region</span>
          <span className="text-gray-900 dark:text-white font-medium">{region}</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 ${currentStatus.color} rounded-full ${status === 'deploying' ? 'animate-pulse' : ''}`}></div>
          <span className={`text-xs font-medium ${currentStatus.textColor}`}>
            {currentStatus.text}
          </span>
        </div>

        <span className="text-xs text-gray-500">Created {createdAt}</span>
      </div>
    </div>
  );
};

export default DatabaseCard;

